/* eslint-disable @typescript-eslint/ban-ts-comment */
import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import type { ICategory } from "web/types/Category";
import type { ICategoryCount } from "web/types/CategoryCount";
import { Functionality } from "web/types/Employee";
import {
  ICategoryFunctionalityMenuItem,
  ICategoryMenuItem,
  IFunctionalityMenuItem,
  ISubCategoryMenuItem,
  IUrlFunctionalityMenuItem,
  IUrlMenuItem,
  MenuData,
  MenuItemType,
} from "web/types/Menu";

import type { SubscriptionQuoteConfig } from "web/features/subscriptionQuote/subscriptionQuoteSlice";

import handleCategory from "./handleCategory";
import handleCategoryFunctionality from "./handleCategoryFunctionality";
import handleFunctionality from "./handleFunctionality";
import handleUrl from "./handleUrl";
import handleUrlCategory from "./handleUrlCategory";
import handleUrlFunctionality from "./handleUrlFunctionality";

export interface IHandleMenuByItemTypeArgs {
  menu: MenuData;
  categories: ICategory[];
  productCounts: ICategoryCount[];
  functionalities?: Functionality[];
  mbActiveEnabledSelector?: boolean;
  MBActiveUrl: string;
  MBActiveUrlEn: string;
  MBActiveMenu: string;
  MBActiveMenuEn: string;
  currentLanguage: string;
  msiUrl: string;
  msiUrlEn: string;
  msiCartsEnabledSelector?: boolean;
  msMenuEn: string;
  hasEnabledAnySubscriptions: boolean;
  subscriptionConfig: SubscriptionQuoteConfig;
}

const handleMenuByItemType = ({
  menu,
  categories,
  productCounts,
  functionalities,
  mbActiveEnabledSelector,
  MBActiveUrl,
  MBActiveUrlEn,
  MBActiveMenu,
  MBActiveMenuEn,
  currentLanguage,
  msiUrl,
  msiUrlEn,
  msiCartsEnabledSelector,
  msMenuEn,
  hasEnabledAnySubscriptions,
  subscriptionConfig,
}: IHandleMenuByItemTypeArgs) =>
  menu?.map((current) => {
    switch (current && current.type) {
      case MenuItemType.URL_CATEGORY: {
        return handleUrlCategory({
          menuItem: current as ICategoryMenuItem<MenuItemType.URL_CATEGORY>,
          categories,
          counts: productCounts!,
        });
      }
      case MenuItemType.FUNCTIONALITY: {
        return handleFunctionality({
          menuItem: current as IFunctionalityMenuItem,
          functionalities,
          mbActiveEnabledSelector,
          MBActiveUrl,
          MBActiveUrlEn,
          MBActiveMenu,
          MBActiveMenuEn,
          currentLanguage,
          msiUrl,
          msiUrlEn,
          msiCartsEnabledSelector,
          msMenuEn,
        });
      }
      case MenuItemType.CATEGORY_FUNCTIONALITY: {
        return handleCategoryFunctionality({
          menuItem: current as ICategoryFunctionalityMenuItem,
          hasEnabledAnySubscriptions,
          subscriptionConfig,
        });
      }
      case MenuItemType.URL_FUNCTIONALITY: {
        return handleUrlFunctionality({
          menuItem: current as IUrlFunctionalityMenuItem,
          functionalities,
          msiCartsEnabledSelector,
        });
      }
      case MenuItemType.URL: {
        return handleUrl({
          menuItem: current as IUrlMenuItem,
          currentLanguage,
        });
      }
      case MenuItemType.SUBCATEGORIES:
      case MenuItemType.TILES: {
        const currentTyped = current as ISubCategoryMenuItem<
          MenuItemType.SUBCATEGORIES | MenuItemType.TILES
        >;
        const subCategoriesWithData = isArrayHasItems(currentTyped.submenu)
          ? currentTyped.submenu.map((submenuItem) => {
              if (submenuItem?.type === MenuItemType.CATEGORY) {
                return handleCategory({
                  menuItem:
                    submenuItem as ICategoryMenuItem<MenuItemType.CATEGORY>,
                  categories,
                  counts: productCounts!,
                });
              }

              if (submenuItem?.type === MenuItemType.FUNCTIONALITY) {
                return handleFunctionality({
                  menuItem: submenuItem as IFunctionalityMenuItem,
                  functionalities,
                  mbActiveEnabledSelector,
                  MBActiveUrl,
                  MBActiveUrlEn,
                  MBActiveMenu,
                  MBActiveMenuEn,
                  currentLanguage,
                  msiUrl,
                  msiUrlEn,
                  msiCartsEnabledSelector,
                  msMenuEn,
                });
              }

              if (submenuItem?.type === MenuItemType.URL_FUNCTIONALITY) {
                return handleUrlFunctionality({
                  menuItem: submenuItem as IUrlFunctionalityMenuItem,
                  functionalities,
                  msiCartsEnabledSelector,
                });
              }

              return submenuItem;
            })
          : [];
        return {
          ...current,
          submenu: subCategoriesWithData,
        };
      }
      default: {
        // @ts-ignore
        return current?.title && current?.url ? current : null;
      }
    }
  });

export default handleMenuByItemType;
